/**
 * Utility functions for PeersCaller library
 */

import type { PeersCallerError } from "../types";

/**
 * Generate a unique ID for various purposes
 */
export function generateId(prefix = ""): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}_${timestamp}_${random}` : `${timestamp}_${random}`;
}

/**
 * Generates a unique peer ID
 */
export const generatePeerId = (): string => {
  return `peer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validates a peer ID format
 */
export const isValidPeerId = (peerId: string): boolean => {
  if (!peerId || typeof peerId !== "string") {
    return false;
  }

  // Check if it follows the expected format: peer_timestamp_randomstring
  const regex = /^peer_\d+_[a-z0-9]{1,9}$/;
  return regex.test(peerId);
};

/**
 * Formats media constraints for getUserMedia
 */
export const formatMediaConstraints = (options: {
  video?: boolean | MediaTrackConstraints;
  audio?: boolean | MediaTrackConstraints;
}): MediaStreamConstraints => {
  return {
    video: options.video ?? true,
    audio: options.audio ?? true,
  };
};

/**
 * Format error messages consistently
 */
export function formatError(
  type: PeersCallerError,
  message: string,
  details?: any
): Error {
  const formattedMessage = `[${type}] ${message}`;
  const error = new Error(formattedMessage);

  if (details) {
    (error as any).details = details;
  }

  return error;
}

/**
 * Check if browser supports required WebRTC features
 */
export function checkWebRTCSupport(): {
  supported: boolean;
  features: {
    getUserMedia: boolean;
    getDisplayMedia: boolean;
    RTCPeerConnection: boolean;
    MediaRecorder: boolean;
    WebSocket: boolean;
  };
  missingFeatures: string[];
} {
  const features = {
    getUserMedia: !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    ),
    getDisplayMedia: !!(
      navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
    ),
    RTCPeerConnection: !!window.RTCPeerConnection,
    MediaRecorder: typeof MediaRecorder !== "undefined",
    WebSocket: typeof WebSocket !== "undefined",
  };

  const missingFeatures = Object.entries(features)
    .filter(([, supported]) => !supported)
    .map(([feature]) => feature);

  return {
    supported: missingFeatures.length === 0,
    features,
    missingFeatures,
  };
}

/**
 * Checks if WebRTC is supported in the current browser
 */
export const isWebRTCSupported = (): boolean => {
  try {
    return !!(
      typeof window !== "undefined" &&
      typeof window.RTCPeerConnection !== "undefined" &&
      typeof navigator !== "undefined" &&
      typeof navigator.mediaDevices !== "undefined" &&
      typeof navigator.mediaDevices.getUserMedia !== "undefined"
    );
  } catch (error) {
    return false;
  }
};

/**
 * Get optimal ICE servers configuration
 */
export function getDefaultIceServers(): RTCIceServer[] {
  return [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:stun3.l.google.com:19302" },
  ];
}
